// =====================================================================
// CSV utilities + Sparkline component for Analytics tabs.
// Keep small and dependency-free.
// =====================================================================

/** Quote a single CSV cell per RFC 4180. */
function csvCell(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Build a CSV string from {headers:[], rows:[[]]} */
export function buildCSV({ headers, rows }) {
  const head = (headers || []).map(csvCell).join(",");
  const body = (rows || []).map(r => r.map(csvCell).join(",")).join("\n");
  return head ? `${head}\n${body}\n` : `${body}\n`;
}

/** Trigger a browser download of the given CSV blob. */
export function downloadCSV(filename, csvText) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
}

// ---------------------------------------------------------------------
// Sparkline — inline SVG line chart for a flat number[]. Renders ~120×30
// by default. `data` should be 2+ points. Values outside [0..max] are
// clamped to the visible band. Used by AttendanceTab to show per-service
// attendance % over time.
// ---------------------------------------------------------------------
import React from "react";

export function Sparkline({ data, width = 120, height = 30, color = "var(--kc-accent)", testId }) {
  if (!Array.isArray(data) || data.length < 2) {
    return (
      <svg width={width} height={height} role="img" aria-label="trend-empty" data-testid={testId}>
        <line x1="0" y1={height - 1} x2={width} y2={height - 1} stroke="color-mix(in srgb, var(--kc-accent) 20%, transparent)" />
      </svg>
    );
  }
  const max = Math.max(100, ...data);
  const min = 0;
  const stepX = width / (data.length - 1);
  const pts = data.map((v, i) => {
    const clamped = Math.min(max, Math.max(min, v));
    const y = height - ((clamped - min) / (max - min || 1)) * (height - 2) - 1;
    return `${(i * stepX).toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={width} height={height} role="img" aria-label="trend" data-testid={testId}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
      {/* Last-point dot for emphasis */}
      <circle
        cx={(data.length - 1) * stepX}
        cy={height - ((data[data.length - 1] - min) / (max - min || 1)) * (height - 2) - 1}
        r="2.2"
        fill={color}
      />
    </svg>
  );
}
